import { V3_SUBGRAPH_URLS } from 'config/constants/endpoints'
import { useActiveChainId } from 'hooks/useActiveChainId'
import EchodexTable from 'components/EchodexTable/EchodexTable'
import useSearchPagination from 'hooks/useSearchPagination'
import { getBlockExploreLink } from 'utils'
import Link from 'next/link'
import { formatAmount } from '@echodex/utils/formatInfoNumbers'
import DoubleCurrencyLogo from 'components/Logo/DoubleLogo'
import Pagination from 'components/Pagination/Pagination'
import CircleLoader from 'components/Loader/CircleLoader'
import { convertToCurrency } from 'hooks/useConvertToCurrency'
import { V3AllPoolsQuery, useV3AllPoolsQuery } from 'graphql/v3/generated/types-and-hooks'
import { useEffect, useState } from 'react'
import { Tag, Text } from '@echodex/uikit'
import { Percent } from '@echodex/swap-sdk-core'
import { ButtonAdd, ButtonFarm } from '../../components/Button'
import V3AprWithFarming from './components/V3AprWithFarming'

const V3Table = () => {
    const { chainId } = useActiveChainId()
    const [beforeNow24h] = useState((Date.now() / 1000 - 60 * 60 * 24).toFixed(0))
    const [skipPage, setSkipPage] = useState(0)
    const { data: allPoolData, isLoading: loading } = useV3AllPoolsQuery(
        { endpoint: V3_SUBGRAPH_URLS[chainId] },
        { beforeNow24h: parseInt(beforeNow24h), skip: skipPage },
    )
    // const filteredPool = (item, valueDebounce) => {
    //     const currency0 = convertToCurrency(chainId, item.token0)
    //     const currency1 = convertToCurrency(chainId, item.token1)
    //     const pairName = `${currency0.symbol}/${currency1.symbol}`
    //     const resultValueDebounce = valueDebounce.toLowerCase()
    //     return (
    //         pairName.toLowerCase().includes(resultValueDebounce) ||
    //         item.token0.id.toLowerCase().includes(resultValueDebounce) ||
    //         item.token1.id.toLowerCase().includes(resultValueDebounce)
    //     )
    // }

    const { currentPage, currentData, handlePagePlus, handlePageMinus, skip, isDisabled } = useSearchPagination(
        allPoolData ? allPoolData.pools : undefined,
        10,
    )
    useEffect(() => {
        setSkipPage(skip)
    }, [skip, setSkipPage])
    return (
        <>
            <div className="flex justify-center">{loading && <CircleLoader size="64px" />}</div>
            <EchodexTable thead={THEAD}>
                {!loading &&
                    currentData &&
                    currentData.map((pair) => {
                        return <TableContent key={pair.id} pair={pair} chainId={chainId} />
                    })}
            </EchodexTable>
            <Pagination
                disabledNext={isDisabled}
                currentPage={currentPage}
                handlePagePlus={handlePagePlus}
                handlePageMinus={handlePageMinus}
            />
        </>
    )
}

export type PoolInfo = V3AllPoolsQuery['pools'][0]

const TableContent = ({ pair, chainId }: { pair: PoolInfo; chainId: number }) => {
    const currency0 = convertToCurrency(chainId, pair.token0)
    const currency1 = convertToCurrency(chainId, pair.token1)
    const [canFarm, setCanFarm] = useState(false)

    let volume24h = 0
    let fee24h = 0
    for (let i = 0; i < pair.poolHourData.length; i++) {
        volume24h += parseFloat(pair.poolHourData[i].volumeUSD)
        fee24h += parseFloat(pair.poolHourData[i].feesUSD)
    }

    return (
        <tr className="even:bg-gray odd:bg-backgroundAlt2 hover:bg-[#252525]">
            <td>
                <div className="pl-4">
                    <a
                        className="gap-1 fa-center"
                        href={getBlockExploreLink(pair.id, 'address', chainId)}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <DoubleCurrencyLogo currency0={currency0} currency1={currency1} size={26} />
                        {`${currency0.symbol}/${currency1.symbol}`}
                        <Tag variant="secondary" ml="5px" outline>
                            <Text fontSize="12px">{new Percent(pair.feeTier, 1_000_000).toSignificant()}%</Text>
                        </Tag>
                    </a>
                </div>
            </td>
            <td>
                <div>
                    {formatAmount(parseFloat(pair.totalValueLockedToken0), { notation: 'standard' })} {currency0.symbol}
                </div>
                <div>
                    {formatAmount(parseFloat(pair.totalValueLockedToken1), { notation: 'standard' })} {currency1.symbol}
                </div>
            </td>
            <td>
                <V3AprWithFarming poolInfo={pair} setCanFarm={setCanFarm} />
            </td>
            <td>
                <div>${formatAmount(parseFloat(pair.totalValueLockedUSD))}</div>
            </td>
            <td>
                <div>${formatAmount(volume24h)}</div>
            </td>
            <td>
                <div>${formatAmount(fee24h)}</div>
            </td>
            <td>
                <div className="fa-center gap-[8px]">
                    <Link
                        href={`/add/${currency0.wrapped ? currency0.wrapped.address : 'ETH'}/${
                            currency1.wrapped ? currency1.wrapped.address : 'ETH'
                        }`}
                        className="plus bg-gray h-[36px] w-[36px] rounded-xl flex-center cursor-pointer "
                    >
                        <ButtonAdd tooltipText="Add V3 Liquidity" />
                    </Link>
                    {canFarm && (
                        <Link
                            href="/farming"
                            className="plus bg-gray h-[36px] w-[36px] rounded-xl flex-center cursor-pointer "
                        >
                            <ButtonFarm tooltipText="Farm Now" />
                        </Link>
                    )}
                </div>
            </td>
        </tr>
    )
}

type thead = {
    name: string
    width?: string
    tooltip?: string
}

const THEAD: thead[] = [
    {
        name: 'Pool',
        tooltip:
            'A token pair can have multiple pools, each with a different swap earnings will be automatically reinvested in your pool.',
    },
    {
        name: 'Liquidity',
    },
    {
        name: 'APR',
        tooltip:
            'Average estimated return based on yearly trading fees from the pool & additional bonus rewards if you participate in the farm',
    },
    {
        name: 'Liquidity (USD)',
    },
    {
        name: 'Volume 24H',
    },
    {
        name: 'Fee 24H (USD)',
    },
    {
        name: 'Action',
    },
]

export default V3Table
